import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { TagService } from "../../services/tag/tag.service";
import { TagActions } from "./tag.actions";
import { ToastService } from "../../services/toast/toast.service";
import { switchMap, mergeMap, exhaustMap, map, catchError, of, from } from "rxjs";
import { ToastType } from "../../models";

@Injectable()
export class TagEffects {
    private actions$ = inject(Actions);
    private tagService = inject(TagService);
    private toastService = inject(ToastService);

    loadTags$ = createEffect(() => this.actions$.pipe(
        ofType(TagActions.loadTags),
        exhaustMap(() => {
            return this.tagService.getTags().pipe(
                map(tags => TagActions.loadTagsSuccess({ tags })),
                catchError(error => of(TagActions.loadTagsFailure({ error })))
            );
        })
    ));

    createTag$ = createEffect(() => this.actions$.pipe(
        ofType(TagActions.createTag),
        exhaustMap(action => {
            return this.tagService.createTag(action.request).pipe(
                map(tag => {
                    this.toastService.showToast("Tag created successfully", ToastType.SUCCESS);
                    return TagActions.createTagSuccess({ tag });
                }),
                catchError(error => {
                    this.toastService.showToast("Failed to create tag", ToastType.ERROR);
                    return of(TagActions.createTagFailure({ error }));
                })
            );
        })
    ));

    updateTags$ = createEffect(() => this.actions$.pipe(
        ofType(TagActions.updateTags),
        exhaustMap(action => {
            return this.tagService.updateTags(action.request).pipe(
                map(tags => {
                    this.toastService.showToast("Tag updated successfully", ToastType.SUCCESS);
                    return TagActions.updateTagsSuccess({ tags });
                }),
                catchError(error => {
                    this.toastService.showToast("Failed to update tag", ToastType.ERROR);
                    return of(TagActions.updateTagsFailure({ error }));
                })
            );
        })
    ));

    deleteTags$ = createEffect(() => this.actions$.pipe(
        ofType(TagActions.deleteTags),
        exhaustMap(action => {
            return this.tagService.deleteTags(action.request).pipe(
                map(() => {
                    this.toastService.showToast("Tag deleted successfully", ToastType.SUCCESS);
                    return TagActions.deleteTagsSuccess({ tagIds: action.request.tag_ids });
                }),
                catchError(error => {
                    this.toastService.showToast("Failed to delete tag", ToastType.ERROR);
                    return of(TagActions.deleteTagsFailure({ error }));
                })
            );
        })
    ));

    loadColours$ = createEffect(() => this.actions$.pipe(
        ofType(TagActions.loadColours),
        switchMap(() => {
            return this.tagService.getColours().pipe(
                map(colours => TagActions.loadColoursSuccess({ colours })),
                catchError(error => of(TagActions.loadColoursFailure({ error })))
            );
        })
    ));

    loadAssetTags$ = createEffect(() => this.actions$.pipe(
        ofType(TagActions.loadAssetTags),
        mergeMap(action => {
            return this.tagService.getAssetTags(action.assetType, action.assetId).pipe(
                map(assetTags => TagActions.loadAssetTagsSuccess({ assetType: action.assetType, assetId: action.assetId, assetTags })),
                catchError(error => of(TagActions.loadAssetTagsFailure({ assetType: action.assetType, assetId: action.assetId, error })))
            );
        })
    ));

    assignAssetTags$ = createEffect(() => this.actions$.pipe(
        ofType(TagActions.assignAssetTags),
        mergeMap(action => {
            return this.tagService.assignAssetTags(action.assetType, action.assetId, { tag_ids: action.tagIds }).pipe(
                map(assetTags => {
                    this.toastService.showToast("Tags assigned successfully", ToastType.SUCCESS);
                    return TagActions.assignAssetTagsSuccess({ assetType: action.assetType, assetId: action.assetId, assetTags });
                }),
                catchError(error => {
                    this.toastService.showToast("Failed to assign tags", ToastType.ERROR);
                    return of(TagActions.assignAssetTagsFailure({ assetType: action.assetType, assetId: action.assetId, tagIds: action.tagIds, error }));
                })
            );
        })
    ));

    unassignAssetTags$ = createEffect(() => this.actions$.pipe(
        ofType(TagActions.unassignAssetTags),
        mergeMap(action => {
            return this.tagService.unassignAssetTags(action.assetType, action.assetId, { tag_ids: action.tagIds }).pipe(
                map(() => {
                    this.toastService.showToast("Tags removed successfully", ToastType.SUCCESS);
                    return TagActions.unassignAssetTagsSuccess({ assetType: action.assetType, assetId: action.assetId, tagIds: action.tagIds });
                }),
                catchError(error => {
                    this.toastService.showToast("Failed to remove tags", ToastType.ERROR);
                    return from([
                        TagActions.unassignAssetTagsFailure({ assetType: action.assetType, assetId: action.assetId, tagIds: action.tagIds, error }),
                        TagActions.loadAssetTags({ assetType: action.assetType, assetId: action.assetId }),
                    ]);
                })
            );
        })
    ));
}
